import { PrivacyBlur } from "#components/privacy-blur"
import { cn } from "#lib/utils"
import React from "react"

const H1 = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h1
      ref={ref}
      className={cn("text-4xl font-bold leading-tight  text-foreground", className)}
      {...props}
    />
  )
)
H1.displayName = "H1"

const H2 = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h2
      ref={ref}
      className={cn("text-3xl font-bold leading-tight  text-foreground", className)}
      {...props}
    />
  )
)
H2.displayName = "H2"

const H3 = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn("text-2xl font-semibold leading-tight  text-foreground", className)}
      {...props}
    />
  )
)
H3.displayName = "H3"

const H4 = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h4
      ref={ref}
      className={cn("text-xl font-semibold leading-tight  text-foreground", className)}
      {...props}
    />
  )
)
H4.displayName = "H4"

const Body = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn("text-base leading-relaxed  text-foreground", className)}
      {...props}
    />
  )
)
Body.displayName = "Body"

const BodySmall = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm leading-relaxed  text-foreground", className)} {...props} />
))
BodySmall.displayName = "BodySmall"

const Caption = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm leading-relaxed text-secondary", className)} {...props} />
  )
)
Caption.displayName = "Caption"

const Muted = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn("text-sm leading-relaxed text-muted-foreground", className)}
      {...props}
    />
  )
)
Muted.displayName = "Muted"

// Additional typography variants for dashboard stats
const StatValue = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "text-3xl sm:text-4xl font-bold leading-tight tracking-tight text-foreground",
        className
      )}
      {...props}
    >
      <PrivacyBlur>{children}</PrivacyBlur>
    </div>
  )
)
StatValue.displayName = "StatValue"

const StatLabel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "text-xs sm:text-sm font-medium leading-relaxed text-muted-foreground",
        className
      )}
      {...props}
    />
  )
)
StatLabel.displayName = "StatLabel"

const MetricValue = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("text-xl sm:text-xl font-semibold leading-tight text-foreground", className)}
      {...props}
    >
      <PrivacyBlur>{children}</PrivacyBlur>
    </div>
  )
)
MetricValue.displayName = "MetricValue"

export { Body, BodySmall, Caption, H1, H2, H3, H4, MetricValue, Muted, StatLabel, StatValue }
